import { apiClient } from './apiClient';

export interface AdminStatistics {
    totalUsers: number;
    totalBios: number;
    totalPodcasts: number;
    totalLinks: number;
    biosByTheme: Record<string, number>;
    podcastsByTheme: Record<string, number>;
}

export interface ActivityFilters {
    searchQuery?: string;
    minBios?: number;
    minPodcasts?: number;
    minTotalContent?: number;
    createdAfter?: string;
    createdBefore?: string;
    lastActivityAfter?: string;
    lastActivityBefore?: string;
    hasContent?: boolean;
    sortBy?: 'email' | 'createdAt' | 'lastActivity' | 'totalContent' | 'totalBios' | 'totalPodcasts';
    sortOrder?: 'asc' | 'desc';
}

export interface UserActivityDetail {
    userId: string;
    email: string;
    totalBios: number;
    totalPodcasts: number;
    totalContent: number;
    createdAt: string;
    lastActivity: string;
    bios: Array<{
        id: string;
        title: string;
        theme: string;
        path: string;
        createdAt: string;
    }>;
    podcasts: Array<{
        id: string;
        name: string;
        theme: string;
        path: string;
        createdAt: string;
    }>;
}


export interface PageAnalyticsFilters {
    searchQuery?: string;
    type?: 'bio' | 'podcast';
    theme?: string;
    minViews?: number;
    minClicks?: number;
    createdAfter?: string;
    createdBefore?: string;
    country?: string;
    device?: string;
    sortBy?: 'title' | 'createdAt' | 'totalViews' | 'totalClicks' | 'userEmail';
    sortOrder?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
}

export interface PageAnalyticsDetail {
    id: string;
    type: 'bio' | 'podcast';
    title: string;
    path: string;
    theme: string;
    userId: string;
    userEmail: string;
    createdAt: string;
    totalViews: number;
    totalClicks: number;
    uniqueCountries: string[];
    deviceBreakdown: Record<string, number>;
    countryBreakdown: Record<string, number>;
    clickTargets: Record<string, number>;
}

export const adminApi = {
    async getStatistics(): Promise<AdminStatistics> {
        const response = await apiClient.get<{ success: boolean; data: AdminStatistics }>('/api/admin/statistics');
        return response.data;
    },

    async getAvailableThemes(): Promise<{ bioThemes: string[]; podcastThemes: string[] }> {
        const response = await apiClient.get<{ success: boolean; data: { bioThemes: string[]; podcastThemes: string[] } }>('/api/admin/themes');
        return response.data;
    },

    async getFilteredActivity(filters: ActivityFilters): Promise<UserActivityDetail[]> {
        const params = new URLSearchParams();

        if (filters.searchQuery) params.append('searchQuery', filters.searchQuery);
        if (filters.minBios !== undefined) params.append('minBios', filters.minBios.toString());
        if (filters.minPodcasts !== undefined) params.append('minPodcasts', filters.minPodcasts.toString());
        if (filters.minTotalContent !== undefined) params.append('minTotalContent', filters.minTotalContent.toString());
        if (filters.createdAfter) params.append('createdAfter', filters.createdAfter);
        if (filters.createdBefore) params.append('createdBefore', filters.createdBefore);
        if (filters.lastActivityAfter) params.append('lastActivityAfter', filters.lastActivityAfter);
        if (filters.lastActivityBefore) params.append('lastActivityBefore', filters.lastActivityBefore);
        if (filters.hasContent !== undefined) params.append('hasContent', filters.hasContent.toString());
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

        const response = await apiClient.get<{ success: boolean; data: UserActivityDetail[] }>(`/api/admin/users/analytics?${params.toString()}`);
        return response.data;
    },

    async getPageAnalytics(filters: PageAnalyticsFilters): Promise<PageAnalyticsDetail[]> {
        const params = new URLSearchParams();

        if (filters.searchQuery) params.append('searchQuery', filters.searchQuery);
        if (filters.type) params.append('type', filters.type);
        if (filters.theme) params.append('theme', filters.theme);
        if (filters.minViews !== undefined) params.append('minViews', filters.minViews.toString());
        if (filters.minClicks !== undefined) params.append('minClicks', filters.minClicks.toString());
        if (filters.createdAfter) params.append('createdAfter', filters.createdAfter);
        if (filters.createdBefore) params.append('createdBefore', filters.createdBefore);
        if (filters.country) params.append('country', filters.country);
        if (filters.device) params.append('device', filters.device);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

        const response = await apiClient.get<{ success: boolean; data: PageAnalyticsDetail[] }>(`/api/admin/pages/analytics?${params.toString()}`);
        return response.data;
    }
};
